/**
 * Runtime parameters: the settings an admin may change while the service is running, without a
 * redeploy. Mirrors `ParameterView` and `ParameterChange` from the allocation service — see
 * README-parameters.md there for the contract.
 *
 * Two things about this shape are easy to get wrong:
 *
 * - It is not an entity. There is no id and no `BaseEntity`, because the list is the service's
 *   *catalog* of settings rather than a table: a parameter nobody has overridden has no row at
 *   all. `name` is the identity, and it is what `/parameters/{key}` is addressed by.
 * - Every value travels as text, whatever `type` says, in both directions. The UI drives its
 *   widget off `type` and converts back to a string before writing.
 */

export enum PARAMETER_TYPE {
    BOOLEAN = 'BOOLEAN',
    INTEGER = 'INTEGER',
    STRING = 'STRING',
    DURATION = 'DURATION',
    STRING_LIST = 'STRING_LIST',
}

/** Which of the three layers answered for the value currently in force. */
export enum PARAMETER_SOURCE {
    /** An admin set it here. The only source a reset has anything to drop. */
    DATABASE = 'DATABASE',
    /** A config source answered: the properties file, a system property or an env var. */
    PROPERTIES = 'PROPERTIES',
    /** Nothing was configured anywhere, so the value declared in code is in force. */
    DEFAULT = 'DEFAULT',
}

export interface Parameter {
    /** The catalog constant, e.g. `SEARCH_MAX_PAGE_SIZE`. The stable identity of the setting. */
    name: string;
    /** The name in application.properties, e.g. `cas.search.max-page-size`. */
    property: string;
    type: PARAMETER_TYPE;
    description: string;
    /** What the service will actually use right now. */
    value: string;
    source: PARAMETER_SOURCE;
    /** The value declared in code, so a reset can say what it will give. */
    defaultValue: string;
    /**
     * What the config sources hold, reported even when a database override is winning.
     *
     * Explicitly `null` rather than absent when nothing is configured — the service serializes
     * the nulls — so test it with `== null` or `??`, never with `=== undefined`. Same for the
     * two fields below.
     */
    propertiesValue?: string | null;
    /** Which config source that was — only meaningful when `source` is PROPERTIES. */
    configSourceName?: string | null;
    /** Set when `value` does not parse as `type`, which happens if a key's type changed after
     * a value had already been stored against the old one. */
    error?: string | null;
}

/** One audited change to one parameter, newest first as the endpoint returns them. */
export interface ParameterChange {
    revision: number;
    changedAt: string;
    /** A principal, `system` for a change with no user behind it, or null when the change
     * predates attribution — including the synthetic baseline revision. */
    username?: string | null;
    type: 'ADD' | 'MOD' | 'DEL';
    /** Null when the change removed the override. */
    value?: string | null;
}

/**
 * Shown under the input, so an admin knows what the service will accept before it says 400.
 * Kept short deliberately: this sits in a narrow table column, where a long sentence wraps to
 * three lines and pushes the row's own value out of sight.
 */
export const PARAMETER_TYPE_HINTS: Record<PARAMETER_TYPE, string> = {
    [PARAMETER_TYPE.BOOLEAN]: 'true or false.',
    [PARAMETER_TYPE.INTEGER]: 'A whole number.',
    [PARAMETER_TYPE.STRING]: 'Any text.',
    [PARAMETER_TYPE.DURATION]: 'ISO-8601, e.g. PT30M.',
    [PARAMETER_TYPE.STRING_LIST]: 'Comma separated.',
};

/** Bounds of the service's INTEGER, which parses with Integer.parseInt. */
const INT_MIN = -2147483648;
const INT_MAX = 2147483647;

/**
 * ISO-8601 durations as java.time.Duration accepts them: an optional sign, days, then a time
 * part of hours, minutes and fractional seconds. Deliberately no years, months or weeks —
 * Duration.parse rejects those, so accepting them here would only move the error to the server.
 */
const DURATION_PATTERN =
    /^[-+]?P(?=[-+]?\d|T)(?:[-+]?\d+D)?(?:T(?=[-+]?\d)(?:[-+]?\d+H)?(?:[-+]?\d+M)?(?:[-+]?\d+(?:[.,]\d{1,9})?S)?)?$/i;

/**
 * The same rules `ParameterType.parse` applies on the server, so a mistyped value is caught
 * before it costs a round trip. Advisory only: the service validates every write regardless and
 * remains the authority on what is legal.
 *
 * @returns why the value is not legal for the type, or null when it is.
 */
export const validateParameterValue = (type: PARAMETER_TYPE, value: string): string | null => {
    const trimmed = value.trim();

    switch (type) {
        case PARAMETER_TYPE.BOOLEAN:
            // Only the two spellings. Anything else would read as false and silently turn a
            // feature off, which is why the service refuses it too.
            return /^(true|false)$/i.test(trimmed) ? null : 'Enter true or false.';

        case PARAMETER_TYPE.INTEGER: {
            if (!/^[-+]?\d+$/.test(trimmed)) return 'Enter a whole number.';
            const parsed = Number(trimmed);
            return parsed >= INT_MIN && parsed <= INT_MAX
                ? null
                : `Out of range. Must be between ${INT_MIN} and ${INT_MAX}.`;
        }

        case PARAMETER_TYPE.DURATION:
            return DURATION_PATTERN.test(trimmed) ? null : 'Not an ISO-8601 duration, e.g. PT30M.';

        case PARAMETER_TYPE.STRING:
        case PARAMETER_TYPE.STRING_LIST:
            // Any text is legal, and the list type drops its own blank entries.
            return null;

        default:
            return null;
    }
};

/** A STRING_LIST split the way the service splits it, so the UI shows the entries it will store. */
export const parameterListValues = (value: string | undefined): string[] =>
    (value ?? '')
        .split(',')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);

/** True when an admin has set this parameter, which is the only case a reset has work to do. */
export const isOverridden = (parameter: Parameter): boolean =>
    parameter.source === PARAMETER_SOURCE.DATABASE;

/**
 * What the value would become if the override were dropped: the configured value if a config
 * source holds one, otherwise the default declared in code.
 */
export const valueAfterReset = (parameter: Parameter): string =>
    parameter.propertiesValue ?? parameter.defaultValue;
